import { forwardRef, InputHTMLAttributes, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'

export interface GridCheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  indeterminate?: boolean
}

/**
 * Token-based 18px square checkbox from the table design system:
 * accent fill when checked (ink checkmark), accent dash when indeterminate.
 * Distinct from the legacy Material `Checkbox` — do not conflate.
 */
export const GridCheckbox = forwardRef<HTMLInputElement, GridCheckboxProps>(
  ({ indeterminate = false, className, ...rest }, forwardedRef) => {
    const innerRef = useRef<HTMLInputElement | null>(null)

    const setRef = (node: HTMLInputElement | null) => {
      innerRef.current = node
      if (typeof forwardedRef === 'function') forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    }

    useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = indeterminate
    }, [indeterminate])

    return (
      <Box
        ref={setRef}
        type="checkbox"
        className={className}
        $indeterminate={indeterminate}
        {...rest}
      />
    )
  },
)

GridCheckbox.displayName = 'GridCheckbox'

export default GridCheckbox

const Box = styled.input<{ $indeterminate: boolean }>`
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border: 1.5px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  position: relative;
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
  transition:
    background ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:checked {
    background: ${({ theme }) => theme.colors.accent};
    border-color: ${({ theme }) => theme.colors.accentDeep};
  }
  &:checked::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 1.5px;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.colors.ink};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  ${({ $indeterminate, theme }) =>
    $indeterminate &&
    css`
      background: ${theme.colors.accent};
      border-color: ${theme.colors.accentDeep};
      &::after {
        content: '';
        position: absolute;
        left: 3px;
        top: 7px;
        width: 10px;
        height: 2px;
        background: ${theme.colors.ink};
        border: 0;
        transform: none;
      }
    `}

  &:disabled {
    cursor: default;
  }
`
